<template>
  <div class="p-4">
    <a-button icon="arrow-left" class="mb-3" @click="$router.push('/actuality')">
      {{ $t("action.retour") }}
    </a-button>

    <a-spin :spinning="loading">
      <div v-if="post" class="card p-4">
        <h3>{{ post.title }}</h3>
        <p class="text-muted">{{ formatDate(post.createdAt) }}</p>
        <div v-if="post.coverImage" class="mb-3">
          <img
            :src="settings.resourcesUrl.replace('FILE_PATH', post.coverImage)"
            style="max-width: 320px; border-radius: 8px"
          />
        </div>
        <div v-html="post.desc"></div>
        <div v-if="post.attachments && post.attachments.length" class="mb-3 mt-3">
          <strong>{{ $t("activityClub.attachments") }}:</strong>
          <a-carousel arrows dots-class="slick-dots slick-thumb" class="mt-2">
            <a slot="customPaging" slot-scope="props">
              <img
                v-if="isImageFile(post.attachments[props.i])"
                :src="getFileUrl(post.attachments[props.i])"
              />
              <div v-else class="document-thumbnail">
                <img
                  :src="getDocumentTypeImage(post.attachments[props.i])"
                  class="document-thumbnail-icon"
                />
              </div>
            </a>
            <div
              v-for="(file, i) in post.attachments"
              :key="'att-' + i"
              class="gallery-item"
            >
              <img
                v-if="isImageFile(file)"
                :src="getFileUrl(file)"
                class="gallery-image"
                @click="previewFile(file)"
              />
              <div v-else class="document-preview" @click="previewFile(file)">
                <img
                  :src="getDocumentTypeImage(file)"
                  class="document-type-image"
                />
                <div class="document-name">{{ attachmentName(file) }}</div>
              </div>
            </div>
          </a-carousel>
        </div>
        <a-row class="mt-3" :gutter="16">
          <a-col :span="8">
            <strong>{{ $t("activityClub.activity") }}:</strong> {{ activityLabel() }}
          </a-col>
          <a-col :span="8">
            <strong>{{ $t("activityClub.price") }}:</strong> {{ post.price }}
          </a-col>
          <a-col :span="8">
            <strong>{{ $t("activityClub.registrationStartDate") }}:</strong>
            {{ formatDateOnly(post.registrationStartDate) }}
          </a-col>
          <a-col :span="8">
            <strong>{{ $t("activityClub.maxPlaces") }}:</strong>
            {{ post.maxPlaces != null ? post.maxPlaces : $t("activityClub.unlimited") }}
          </a-col>
          <a-col :span="8">
          <strong>{{ $t("activityClub.registrationStartDate") }}:</strong>
          {{ formatDateOnly(post.registrationStartDate) }}
        </a-col>
        <a-col :span="8">
          <strong>{{ $t("activityClub.clubStartDate") }}:</strong>
          {{ formatDateOnly(post.clubStartDate) }}
        </a-col>
        <a-col :span="8">
          <strong>{{ $t("activityClub.clubEndDate") }}:</strong>
          {{ formatDateOnly(post.clubEndDate) }}
        </a-col>
        <a-col :span="16">
         <strong>{{ $t("actualite.specificFor") }}:</strong> {{ targetAudienceLabel() }}
        </a-col>
        </a-row>

        <div class="px-2 mt-4">
          <span class="px-3">
            <a-icon
              v-if="!post.liked"
              @click="submitLike"
              class="pointer"
              type="heart"
              style="font-size: 20px"
            />
            <a-icon
              v-else
              type="heart"
              @click="submitUnlike"
              class="pointer"
              theme="twoTone"
              two-tone-color="#eb2f96"
              style="font-size: 20px"
            />
            <span
              class="pointer underline"
              style="font-size: 20px; padding-left: 8px"
              @click="showWhoLiked"
            >
              {{ post.total_likes ? post.total_likes : 0 }}
            </span>
          </span>
          <span class="px-3">
            <a-icon type="message" style="font-size: 20px" />
            <span style="font-size: 20px; padding-left: 8px">
              {{ post.comments ? post.comments.length : 0 }}
            </span>
          </span>
        </div>

        <div v-if="enableClubComments" class="comments-toggle-container mt-4">
          <div class="comments-toggle-header">
            <span class="comments-toggle-label">
              {{ $t("activityClub.enableCommentsForThisPost") }}
            </span>
            <a-switch
              :checked="post.enableComments !== false"
              @change="onPostCommentsToggle"
            />
          </div>
          <div class="comments-toggle-description">
            {{ $t("activityClub.enableCommentsForThisPostDesc") }}
          </div>
        </div>

        <div
          v-if="post.comments && post.comments.length"
          class="comment-section mt-3"
        >
          <h5>{{ $t("feedback.comment") }}</h5>
          <span v-for="(comment, i) in post.comments" :key="comment._id || i">
            <a-comment class="px-2">
              <template slot="actions" v-if="comment.type == 'Admin'">
                <span @click="deleteComment(comment._id)">
                  {{ $t("action.supprimer") }}
                </span>
              </template>
              <a slot="author" v-if="comment.type === 'Admin'">{{ schoolName }}</a>
              <a slot="author" style="text-transform: capitalize" v-else>{{ comment.fullName }}</a>
              <a-avatar
                v-if="comment.type === 'Admin'"
                slot="avatar"
                :src="logo"
                alt="image"
              />
              <a-avatar
                v-else-if="comment.photo"
                slot="avatar"
                :src="settings.resourcesUrl.replace('FILE_PATH', comment.photo)"
                alt="image"
              />
              <p slot="content">{{ comment.desc }}</p>
              <a-tooltip slot="datetime">
                <span>{{ commentDateLabel(comment) }}</span>
              </a-tooltip>
            </a-comment>
          </span>
        </div>

        <div
          v-if="post.commentsEnabled !== false"
          class="pt-3 row"
        >
          <a-textarea
            class="col-11"
            :placeholder="$t('smartSchedule.comment')"
            auto-size
            v-model="commentRow"
          />
          <div class="col-1 d-flex">
            <a-button class="px-2" icon="enter" @click="submitComment" />
          </div>
        </div>
      </div>

      <a-tabs v-model="activeTab" class="mt-4">
        <a-tab-pane key="subscribers" :tab="$t('activityClub.subscribers')">
          <div class="mb-3 d-flex">
            <a-input
              v-model="search"
              :placeholder="$t('activityClub.searchByName')"
              style="width: 240px"
              class="mr-2"
              @pressEnter="loadJoinedUsers"
            />
            <a-select v-model="statusFilter" style="width: 160px" @change="loadJoinedUsers">
              <a-select-option value="">{{ $t("activityClub.allStatuses") }}</a-select-option>
              <a-select-option value="pending">{{ $t("activityClub.pending") }}</a-select-option>
              <a-select-option value="accepted">{{ $t("activityClub.accepted") }}</a-select-option>
              <a-select-option value="rejected">{{ $t("activityClub.rejected") }}</a-select-option>
            </a-select>
          </div>
          <a-table
            :columns="columns"
            :data-source="joinedUsers"
            row-key="_id"
            :pagination="false"
            size="small"
          >
            <template slot="status" slot-scope="text">
              <a-tag :color="statusColor(text)">{{ $t('activityClub.' + text) }}</a-tag>
            </template>
            <template slot="actions" slot-scope="text, record">
              <a-button
                v-if="canAccept(record)"
                size="small"
                type="primary"
                class="mr-1"
                :loading="processingJoinId === record._id"
                :disabled="processingJoinId === record._id"
                @click="reviewJoin(record._id, 'accept')"
              >
                {{ $t("activityClub.accept") }}
              </a-button>
              <a-button
                v-if="canReject(record)"
                size="small"
                type="danger"
                :loading="processingJoinId === record._id"
                :disabled="processingJoinId === record._id"
                @click="reviewJoin(record._id, 'reject')"
              >
                {{ $t("activityClub.reject") }}
              </a-button>
              <span
                v-if="!canAccept(record) && !canReject(record)"
                class="text-muted"
              >—</span>
            </template>
          </a-table>
        </a-tab-pane>

        <a-tab-pane key="views" :tab="$t('activityClub.views')">
          <a-tabs v-model="seenTab">
            <a-tab-pane key="seen" :tab="$t('actualite.seenCountLabel', { count: seenParents.length })">
              <table class="seen-table">
                <thead>
                  <tr>
                    <th>{{ $t("actualite.parentLabel") }}</th>
                    <th>{{ $t("actualite.seenAtLabel") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(p, i) in seenParents" :key="'s-' + i">
                    <td>{{ p.parentName }}</td>
                    <td>{{ formatDate(p.seenAt) }}</td>
                  </tr>
                </tbody>
              </table>
            </a-tab-pane>
            <a-tab-pane key="notSeen" :tab="$t('actualite.notSeenCountLabel', { count: notSeenParents.length })">
              <table class="seen-table">
                <thead>
                  <tr><th>{{ $t("actualite.parentLabel") }}</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(p, i) in notSeenParents" :key="'n-' + i">
                    <td>{{ p.parentName }}</td>
                  </tr>
                </tbody>
              </table>
            </a-tab-pane>
          </a-tabs>
        </a-tab-pane>
      </a-tabs>
    </a-spin>

    <a-modal v-model="visibleLikes" :title="$t('actualite.mentionJaimes')">
      <template slot="footer"><div></div></template>
      <a-skeleton avatar :paragraph="{ rows: 4 }" v-if="loadingLikes" />
      <div class="comment-section" v-else>
        <div v-for="(like, i) in likes" :key="i" class="d-flex p-2 item-center">
          <a-avatar
            v-if="like.type === 'Admin'"
            :src="logo"
            alt="image"
            class="mr-3"
          />
          <a-avatar
            v-else-if="like.parent"
            :src="
              settings.resourcesUrl.replace(
                'FILE_PATH',
                (like.parentType && like.parent[like.parentType]
                  ? like.parent[like.parentType].photo
                  : like.parent.photo) || ''
              )
            "
            alt="image"
            class="mr-3"
          />
          <span v-if="like.type == 'Admin'">{{ schoolName }}</span>
          <span v-else style="text-transform: capitalize">{{ like.fullName }}</span>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import apiClient from "@/services/axios";
import { showApiError } from "@/utilities/showApiError";

export default {
  name: "SingleClubPost",
  data() {
    return {
      loading: false,
      post: null,
      joinedUsers: [],
      search: "",
      statusFilter: "",
      activeTab: "subscribers",
      seenTab: "seen",
      seenParents: [],
      notSeenParents: [],
      processingJoinId: null,
      columns: [],
      commentRow: "",
      likes: [],
      visibleLikes: false,
      loadingLikes: true,
      enableClubComments: true,
    };
  },
  computed: {
    ...mapState(["settings", "user"]),
    activeBuildingImage() {
      const images = this.settings?.image;
      if (!Array.isArray(images) || !images.length) return null;
      return (
        images.find((img) => img.db === this.settings.activeBuilding) ||
        images[0] ||
        null
      );
    },
    logo() {
      return this.activeBuildingImage?.logo || null;
    },
    schoolName() {
      return this.activeBuildingImage?.name || "";
    },
  },
  created() {
    const building = this.user?.building?.filter(
      (el) => el.dbName == this.settings.activeBuilding
    )[0];
    if (building && building.enableClubComments !== undefined) {
      this.enableClubComments = building.enableClubComments;
    }
    this.columns = [
      { title: this.$t("activityClub.parentName"), dataIndex: "parentName" },
      { title: this.$t("activityClub.studentName"), dataIndex: "studentName" },
      { title: this.$t("activityClub.status"), dataIndex: "status", scopedSlots: { customRender: "status" } },
      { title: this.$t("activityClub.subscribedAt"), dataIndex: "subscribedAt", customRender: (d) => this.formatDate(d) },
      { title: this.$t("action.actions"), scopedSlots: { customRender: "actions" } },
    ];
    this.loadPost();
    this.loadJoinedUsers();
    this.loadSeenStatus();
  },
  methods: {
    activityLabel() {
    if (!this.post) return "-";
    if (this.post.activity) {
      return this.post.activity.designation || this.post.activity.title || "-";
    }
    return this.post.activityCustomName || "-";
  },
  targetAudienceLabel() {
    if (!this.post) return "-";
    if (this.post.students && this.post.students.length) {
      return this.$t("actualite.students") + " (" + this.post.students.length + ")";
    }
    if (this.post.levels && this.post.levels.length) {
      return this.$t("actualite.levels") + ": " + this.post.levels.join(", ");
    }
    if (this.post.classrooms && this.post.classrooms.length) {
      const classNames = this.post.classrooms
        .map((c) => (c && c.name ? c.name : c))
        .join(", ");
      return this.$t("actualite.classrooms") + ": " + classNames;
    }
    if (this.post.activities && this.post.activities.length) {
      const names = this.post.activities
        .map((a) => (a && a.designation ? a.designation : a))
        .join(", ");
      return this.$t("actualite.activities") + ": " + names;
    }
    return this.$t("actualite.tous");
  },

    attachmentName(att) {
    if (!att) return "";
    const fullName = att.split("/").pop();
    const separatorIndex = fullName.indexOf("--");
    return separatorIndex !== -1
      ? fullName.substring(separatorIndex + 2)
      : fullName;
  },
    isImageFile(file) {
      if (!file) return false;
      const ext = file.split(".").pop().toLowerCase();
      return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
    },
    getDocumentTypeImage(file) {
      const ext = file.split(".").pop().toLowerCase();
      const imageMap = {
        pdf: "/resources/images/pdf-icon.png",
        doc: "/resources/images/word-icon.png",
        docx: "/resources/images/word-icon.png",
      };
      return imageMap[ext];
    },
    getFileUrl(file) {
      return this.settings.resourcesUrl.replace("FILE_PATH", file);
    },
    previewFile(file) {
      window.open(this.getFileUrl(file), "_blank");
    },
    formatDate(d) {
      return d ? moment(d).format("DD/MM/YYYY HH:mm") : "";
    },
    formatDateOnly(d) {
      return d ? moment(d).format("DD/MM/YYYY") : "";
    },
    commentDateLabel(comment) {
      const d = comment.createdAt ? moment(comment.createdAt).format("DD/MM/YYYY") : "";
      const rel = comment.relativeTime
        ? this.relativeTimeLabel(comment.relativeTime)
        : comment.createdAt
          ? moment(comment.createdAt).fromNow()
          : "";
      return rel ? `${d} (${rel})` : d;
    },
    relativeTimeLabel(rt) {
      if (!rt || rt.unit == null) return "";
      const key = rt.count === 1 ? `time.${rt.unit}Ago` : `time.${rt.unit}sAgo`;
      return this.$te(key) ? this.$t(key, { count: rt.count }) : "";
    },
    statusColor(status) {
      if (status === "accepted") return "green";
      if (status === "rejected") return "red";
      return "orange";
    },
    canAccept(record) {
      return record.status === "pending" || record.status === "rejected";
    },
    canReject(record) {
      return record.status === "pending" || record.status === "accepted";
    },
    adjustPostCounts(previousStatus, newStatus) {
      if (!this.post || previousStatus === newStatus) return;
      const dec = (status) => {
        if (status === "pending") {
          this.post.pendingCount = Math.max(0, (this.post.pendingCount || 0) - 1);
        } else if (status === "accepted") {
          this.post.acceptedCount = Math.max(0, (this.post.acceptedCount || 0) - 1);
        } else if (status === "rejected") {
          this.post.rejectedCount = Math.max(0, (this.post.rejectedCount || 0) - 1);
        }
      };
      const inc = (status) => {
        if (status === "pending") {
          this.post.pendingCount = (this.post.pendingCount || 0) + 1;
        } else if (status === "accepted") {
          this.post.acceptedCount = (this.post.acceptedCount || 0) + 1;
        } else if (status === "rejected") {
          this.post.rejectedCount = (this.post.rejectedCount || 0) + 1;
        }
      };
      dec(previousStatus);
      inc(newStatus);
    },
    async loadPost() {
      this.loading = true;
      try {
        const { data } = await apiClient.get("/activity-club/" + this.$route.params.id);
        this.post = data;
      } catch (err) {
        showApiError(this, err);
      } finally {
        this.loading = false;
      }
    },
    async loadJoinedUsers() {
      try {
        const { data } = await apiClient.get(
          "/activity-club/" + this.$route.params.id + "/joined-users",
          { params: { status: this.statusFilter || undefined, search: this.search || undefined } }
        );
        this.joinedUsers = data.data || [];
      } catch (err) {
        showApiError(this, err);
      }
    },
    async loadSeenStatus() {
      try {
        const { data } = await apiClient.get(
          "/activity-club/" + this.$route.params.id + "/seen-status"
        );
        this.seenParents = data.seenParents || [];
        this.notSeenParents = data.notSeenParents || [];
      } catch (err) {
        showApiError(this, err);
      }
    },
    async reviewJoin(joinId, action) {
      const row = this.joinedUsers.find((j) => j._id === joinId);
      if (!row) return;
      if (action === "accept" && !this.canAccept(row)) return;
      if (action === "reject" && !this.canReject(row)) return;

      const newStatus = action === "accept" ? "accepted" : "rejected";
      const previousStatus = row.status;
      row.status = newStatus;
      this.processingJoinId = joinId;
      this.adjustPostCounts(previousStatus, newStatus);

      try {
        const url =
          "/activity-club/" +
          this.$route.params.id +
          "/join/" +
          joinId +
          "/" +
          (action === "accept" ? "accept" : "reject");
        await apiClient.patch(url);
        this.$message.success(this.$t("success.pubModif"));
      } catch (err) {
        row.status = previousStatus;
        this.adjustPostCounts(newStatus, previousStatus);
        showApiError(this, err);
      } finally {
        this.processingJoinId = null;
      }
    },
    submitLike() {
      if (!this.post) return;
      this.post.liked = true;
      this.post.total_likes = (this.post.total_likes || 0) + 1;
      apiClient
        .post("/activity-club/" + this.$route.params.id + "/like", {
          type: "Admin",
          fullName: this.user?.userName,
        })
        .catch((e) => {
          this.post.liked = false;
          this.post.total_likes = Math.max(0, (this.post.total_likes || 1) - 1);
          showApiError(this, e);
        });
    },
    submitUnlike() {
      if (!this.post) return;
      this.post.liked = false;
      this.post.total_likes = Math.max(0, (this.post.total_likes || 1) - 1);
      apiClient
        .delete("/activity-club/" + this.$route.params.id + "/like", {
          data: { type: "Admin" },
        })
        .catch((e) => {
          this.post.liked = true;
          this.post.total_likes = (this.post.total_likes || 0) + 1;
          showApiError(this, e);
        });
    },
    async submitComment() {
      if (!this.commentRow || !this.commentRow.trim()) return;
      try {
        const { data } = await apiClient.post(
          "/activity-club/" + this.$route.params.id + "/comment",
          {
            desc: this.commentRow,
            type: "Admin",
            fullName: this.user?.userName,
          }
        );
        if (!this.post.comments) {
          this.post.comments = [];
        }
        if (data?.entity) {
          this.post.comments.push(data.entity);
        }
        this.commentRow = "";
      } catch (e) {
        showApiError(this, e);
      }
    },
    async deleteComment(commentId) {
      try {
        await apiClient.delete(
          "/activity-club/" +
            this.$route.params.id +
            "/comment/" +
            commentId
        );
        if (this.post.comments) {
          this.post.comments = this.post.comments.filter((c) => c._id !== commentId);
        }
      } catch (e) {
        showApiError(this, e);
      }
    },
    showWhoLiked() {
      this.loadingLikes = true;
      this.visibleLikes = true;
      apiClient
        .get("/activity-club/" + this.$route.params.id + "/likes")
        .then(({ data }) => {
          this.likes = data.likes || [];
        })
        .catch((e) => showApiError(this, e))
        .finally(() => {
          this.loadingLikes = false;
        });
    },
    async onPostCommentsToggle(checked) {
      try {
        await apiClient.put("/activity-club/" + this.$route.params.id + "/update", {
          enableComments: checked,
        });
        this.post.enableComments = checked;
        this.post.commentsEnabled = this.enableClubComments && checked;
        this.$message.success(this.$t("success.changementEffectue"));
      } catch (e) {
        showApiError(this, e);
      }
    },
  },
};
</script>

<style scoped>
.seen-table {
  width: 100%;
  border-collapse: collapse;
}
.seen-table th,
.seen-table td {
  border: 1px solid #eee;
  padding: 8px;
}

.comments-toggle-container {
  margin-bottom: 16px;
  padding: 16px;
  background-color: #fafafa;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}

.comments-toggle-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.comments-toggle-label {
  font-size: 14px;
  font-weight: 500;
  margin-right: 12px;
}

.comments-toggle-description {
  color: #8c8c8c;
  font-size: 12px;
}

.comment-section {
  overflow-y: auto;
  max-height: 300px;
}

.item-center {
  align-items: center;
}

.underline {
  text-decoration: underline;
}

.pointer {
  cursor: pointer;
}

.ant-carousel >>> .slick-dots {
  height: auto;
  position: relative;
  bottom: 0;
  margin: 10px 0;
}

.ant-carousel >>> .slick-slide img {
  border: 5px solid #fff;
  display: block;
  margin: auto;
  max-width: 80%;
}

.ant-carousel >>> .slick-thumb {
  bottom: 0;
  padding: 0;
}

.ant-carousel >>> .slick-thumb li {
  width: 80px;
  height: 70px;
  margin: 0 4px;
}

.ant-carousel >>> .slick-thumb li img {
  width: 100%;
  height: 100%;
  filter: grayscale(100%);
  border-radius: 4px;
}

.ant-carousel >>> .slick-thumb li.slick-active img {
  filter: grayscale(0%);
}

.gallery-item {
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 20px;
}

.gallery-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 15px;
  cursor: pointer;
}

.document-preview {
  text-align: center;
  cursor: pointer;
  padding: 20px;
  border: 1px solid #e8e8e8;
  width: 350px;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(194, 194, 194, 0.1);
}

.document-type-image {
  width: 100px;
  height: 100px;
  object-fit: contain;
}

.document-name {
  font-size: 16px;
  color: #333;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-thumbnail {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.document-thumbnail-icon {
  width: 40px;
  height: 40px;
  object-fit: contain;
}
</style>
